import { useCallback, useRef, useState } from 'react';
import { z } from 'zod';
import _set from 'lodash/set';
import _omit from 'lodash/omit';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _mergeWith from 'lodash/mergeWith';
import _isPlainObject from 'lodash/isPlainObject';

import { formUtils } from 'utils/methods';

/**
 * @template Schema
 * @param {Object} args
 * @param {Schema} args.dataSchema - Zod schema for validating form data.
 * @param {import('zod').z.infer<Schema>} args.defaultData - Initial data for the form.
 * @returns {{
 *  data: import('zod').z.infer<Schema>,
 *  initialData: import('zod').z.infer<Schema>,
 *  errors: Partial<import('zod').z.infer<Schema>>,
 *  isDirty: boolean,
 *  onReset: () => void,
 *  onChange: (updates: Partial<import('zod').z.infer<Schema>>, runValidation?: boolean) => void,
 *  onValidate: (dataset?: Partial<import('zod').z.infer<Schema>>) => boolean,
 *  onSetInitialData: (initialFormData: import('zod').z.infer<Schema>) => void
 *  onSetSchema: (schema: z.ZodObject) => void
 * }}
 */
export default function useForm({
  defaultData = {},
  dataSchema = z.object({})
} = {}) {
  const [data, setData] = useState(defaultData);
  const [schema, setSchema] = useState(dataSchema);
  const [errors, setErrors] = useState({});
  const initialDataRef = useRef(defaultData);

  /**
   * @param {Partial<Schema>} updates - Dataset to validate.
   * @param {Schema} formData - Complete form-data.
   * @returns {boolean} - Returns true if validation passes, false otherwise.
   * @description Validates the updates against the schema and updates errors state.
   */
  const onValidate = useCallback(
    (updates = {}, formData) => {
      const paths = formUtils.getErrorPaths(updates);
      const issuesMap = formUtils.validate(formData || updates, schema);

      setErrors(prevErrors => {
        let newErrors = { ...prevErrors };
        paths.forEach(path => {
          if (path in issuesMap) {
            newErrors = _set(newErrors, path, issuesMap[path]);
          } else {
            newErrors = _omit(newErrors, path);
          }
        });

        return formUtils.getNonEmptyDeepCopy(newErrors);
      });

      return _isEmpty(issuesMap);
    },
    [schema]
  );

  /**
   * @param {Partial<Schema>} [updates={}] - Updates to apply to the form data.
   * @param {boolean} [runValidation=true] - Whether to run validation after updating data.
   * @description Merges updates into the current form data and optionally validates the updated data.
   */
  const onChange = useCallback(
    (updates = {}, runValidation = true) => {
      if (!_isPlainObject(updates) || _isEmpty(updates)) {
        return;
      }

      setData(prevData => {
        const newData = _mergeWith({}, prevData, updates, (_, v) => v);
        if (runValidation) {
          onValidate(updates, newData);
        }
        return newData;
      });
    },
    [onValidate]
  );

  /** @description Resets the form data to its initial state. */
  const onReset = useCallback(() => setData(initialDataRef.current), []);

  /** @param {Schema} initialFormData - Initial data to set for the form. */
  const onSetInitialData = useCallback(initialFormData => {
    initialDataRef.current = initialFormData;
    setData(initialFormData);
    setErrors({});
  }, []);

  return {
    data,
    initialData: initialDataRef.current,
    errors,
    isDirty: !_isEqual(data, initialDataRef.current),
    onReset,
    onChange,
    onValidate,
    onSetInitialData,
    onSetSchema: setSchema
  };
}
